/**
 * useIsMobile Hook
 * Detects if the user is on a mobile device based on screen width
 * Uses Joy UI breakpoints: xs < 600px, sm >= 600px, md >= 900px
 */

import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

/**
 * Hook to detect mobile/tablet/desktop based on screen width
 * @param mobileBreakpoint - Max width for mobile (default: 768px)
 * @param tabletBreakpoint - Max width for tablet (default: 1024px)
 */
export function useIsMobile(mobileBreakpoint = 768, tabletBreakpoint = 1024): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Use visualViewport for DevTools device emulation support, fallback to document width
    const width = typeof window !== 'undefined' 
      ? (window.visualViewport?.width || document.documentElement.clientWidth || window.innerWidth)
      : 1200;
    return {
      isMobile: width < mobileBreakpoint,
      isTablet: width >= mobileBreakpoint && width < tabletBreakpoint,
      isDesktop: width >= tabletBreakpoint,
      width
    };
  });

  useEffect(() => {
    const handleResize = () => {
      // Use visualViewport for DevTools device emulation support, fallback to document width
      const width = window.visualViewport?.width || document.documentElement.clientWidth || window.innerWidth;
      setDeviceInfo({
        isMobile: width < mobileBreakpoint,
        isTablet: width >= mobileBreakpoint && width < tabletBreakpoint,
        isDesktop: width >= tabletBreakpoint,
        width
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call immediately to set initial state
    handleResize();

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint, tabletBreakpoint]);

  return deviceInfo;
}

/**
 * Simple hook that just returns boolean for mobile
 */
export function useIsMobileSimple(breakpoint = 768): boolean {
  const { isMobile } = useIsMobile(breakpoint);
  return isMobile;
}
